import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { User } from 'types/interfaces/user';
import { AdminTransactionHistoryResponse, AdminTransactionHistoryRequest } from 'types/interfaces/transaction';
import {
  CronJobHistoryResponse,
  CronJobHistoryRequest,
  CronJobTypesResponse,
  TriggerCronJobRequest,
  TriggerCronJobResponse
} from 'types/interfaces/cronjob';
import {
  UserDetailsResponse,
  AdjustPointsRequest,
  AdjustPointsResponse,
  ImpersonateUserRequest,
  ImpersonateUserResponse,
  ExitImpersonationResponse,
  BanUserRequest,
  BanUserResponse,
  UnbanUserRequest,
  UnbanUserResponse,
  DeleteAccountRequest,
  DeleteAccountResponse,
  UserAccountStatus,
  WriterRoleResponse,
  AdminEliteStatusResponse,
  AdminManageEliteRequest,
  AdminManageEliteResponse
} from 'types/interfaces/admin/userDetails';
import {
  ActiveTraderDiscountSettings,
  UpdateActiveTraderDiscountRequest
} from 'types/interfaces/economic';
import {
  MaintenanceModeSettings,
  UpdateMaintenanceModeRequest,
  UpdateMaintenanceModeResponse
} from 'types/interfaces/admin/maintenanceMode';
import { AdminBlocksResponse, AdminBlocksRequest } from 'types/interfaces/admin/blocks';

export interface AdminUser extends User {
  lastLogin?: string;
  status?: UserAccountStatus;
  isAdmin?: boolean;
  isBanned?: boolean;
  isElite?: boolean;
}

export interface UsersListResponse {
  users: AdminUser[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    pageSize: number;
  };
}

export interface TradeFeeSettings {
  tradeFeePercentage: number;
  updatedAt: string;
}

export interface UpdateTradeFeeRequest {
  tradeFeePercentage: number;
}

export interface UpdateTradeFeeResponse {
  success: boolean;
  tradeFeePercentage: number;
}

export interface MonthlyFeeSettings {
  monthlyFeePoints: number;
  updatedAt: string;
}

export interface UpdateMonthlyFeeRequest {
  monthlyFeePoints: number;
}

export interface UpdateMonthlyFeeResponse {
  success: boolean;
  monthlyFeePoints: number;
}

export interface UpdateActiveTraderDiscountResponse {
  success: boolean;
  activeTraderDiscountPercentage: number;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQuery,
  tagTypes: ['AdminUsers', 'TradeFee', 'MonthlyFee', 'ActiveTraderDiscount', 'MaintenanceMode', 'AdminTransactions', 'AdminBlocks', 'CronJobs', 'UserDetails', 'EliteStatus', 'LanguageMultipliers'],
  endpoints: (builder) => ({
    getUsers: builder.query<UsersListResponse, { page?: number; pageSize?: number; search?: string; filter?: string; address?: string; country?: string }>({
      query: ({ page = 1, pageSize = 50, search = '', filter, address = '', country = '' }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        if (search) params.set('search', search);
        if (filter && filter !== 'all') params.set('filter', filter);
        if (address) params.set('address', address);
        if (country) params.set('country', country);
        return {
          url: `/admin/users?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['AdminUsers'],
    }),
    getUserCountries: builder.query<string[], void>({
      query: () => ({
        url: '/admin/users/countries',
        method: 'GET',
        requiresAuth: true,
      }),
    }),
    getUserById: builder.query<AdminUser, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminUsers', 'UserDetails'],
    }),
    getTradeFee: builder.query<TradeFeeSettings, void>({
      query: () => ({
        url: '/admin/settings/trade-fee',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['TradeFee'],
    }),
    updateTradeFee: builder.mutation<UpdateTradeFeeResponse, UpdateTradeFeeRequest>({
      query: (body) => ({
        url: '/admin/settings/trade-fee',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeFee'],
    }),
    getMonthlyFee: builder.query<MonthlyFeeSettings, void>({
      query: () => ({
        url: '/admin/settings/monthly-fee',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['MonthlyFee'],
    }),
    updateMonthlyFee: builder.mutation<UpdateMonthlyFeeResponse, UpdateMonthlyFeeRequest>({
      query: (body) => ({
        url: '/admin/settings/monthly-fee',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['MonthlyFee'],
    }),
    getActiveTraderDiscount: builder.query<ActiveTraderDiscountSettings, void>({
      query: () => ({
        url: '/admin/settings/active-trader-discount',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['ActiveTraderDiscount'],
    }),
    updateActiveTraderDiscount: builder.mutation<UpdateActiveTraderDiscountResponse, UpdateActiveTraderDiscountRequest>({
      query: (body) => ({
        url: '/admin/settings/active-trader-discount',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTraderDiscount'],
    }),
    getMaintenanceMode: builder.query<MaintenanceModeSettings, void>({
      query: () => ({
        url: '/admin/settings/maintenance-mode',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['MaintenanceMode'],
    }),
    updateMaintenanceMode: builder.mutation<UpdateMaintenanceModeResponse, UpdateMaintenanceModeRequest>({
      query: (body) => ({
        url: '/admin/settings/maintenance-mode',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['MaintenanceMode'],
    }),
    getAdminTransactions: builder.query<AdminTransactionHistoryResponse, AdminTransactionHistoryRequest>({
      query: ({ page = 1, pageSize = 50, search = '', typeFilter = 'all' }) => ({
        url: `/admin/transactions?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}&typeFilter=${typeFilter}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminTransactions'],
    }),
    getAdminBlocks: builder.query<AdminBlocksResponse, AdminBlocksRequest>({
      query: ({ page = 1, pageSize = 50 }) => ({
        url: `/admin/blocks?page=${page}&pageSize=${pageSize}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminBlocks'],
    }),
    getCronJobHistory: builder.query<CronJobHistoryResponse, CronJobHistoryRequest>({
      query: ({ page = 1, pageSize = 50, typeFilter = 'all', statusFilter = 'all' }) => ({
        url: `/admin/cronjobs?page=${page}&pageSize=${pageSize}&typeFilter=${typeFilter}&statusFilter=${statusFilter}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['CronJobs'],
    }),
    getCronJobTypes: builder.query<CronJobTypesResponse, void>({
      query: () => ({
        url: '/admin/cronjobs/types',
        method: 'GET',
        requiresAuth: true,
      }),
    }),
    triggerCronJob: builder.mutation<TriggerCronJobResponse, { type: string; body: TriggerCronJobRequest }>({
      query: ({ type, body }) => ({
        url: `/admin/cronjobs/${type}/trigger`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['CronJobs'],
    }),

    // User Details Admin Panel Endpoints
    getUserDetails: builder.query<UserDetailsResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['UserDetails'],
    }),
    adjustUserPoints: builder.mutation<AdjustPointsResponse, { userId: number; body: AdjustPointsRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/adjust-points`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminTransactions'],
    }),
    impersonateUser: builder.mutation<ImpersonateUserResponse, { userId: number; body: ImpersonateUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/impersonate`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
    }),
    exitImpersonation: builder.mutation<ExitImpersonationResponse, void>({
      query: () => ({
        url: '/admin/impersonate/exit',
        method: 'POST',
        requiresAuth: true,
      }),
    }),
    banUser: builder.mutation<BanUserResponse, { userId: number; body: BanUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/ban`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    unbanUser: builder.mutation<UnbanUserResponse, { userId: number; body: UnbanUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/unban`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    deleteUserAccount: builder.mutation<DeleteAccountResponse, { userId: number; body: DeleteAccountRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/delete-account`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    grantWriterRole: builder.mutation<WriterRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/writer`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    revokeWriterRole: builder.mutation<WriterRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/writer`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    // Partner API access: grant also provisions a hashed key on the backend.
    // Response body is unused by the UI (handlers refetch user details).
    grantPartnerApiRole: builder.mutation<PartnerApiRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/partner-api`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    revokePartnerApiRole: builder.mutation<PartnerApiRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/partner-api`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),

    // Elite Status Management
    getAdminEliteStatus: builder.query<AdminEliteStatusResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/elite`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_result, _error, userId) => [{ type: 'EliteStatus', id: userId }],
    }),
    manageUserElite: builder.mutation<AdminManageEliteResponse, { userId: number; body: AdminManageEliteRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/elite`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        'UserDetails',
        'AdminUsers',
        { type: 'EliteStatus', id: userId },
      ],
    }),
    // Language multiplier management
    getLanguageMultipliers: builder.query<LanguageMultiplier[], void>({
      query: () => ({
        url: '/admin/language-multipliers',
        method: 'GET',
        requiresAuth: true,
      }),
      // Backend wraps this response in a { success, data } envelope
      // (LanguageMultiplierController.getAll); normalize to the bare array.
      transformResponse: (response: any): LanguageMultiplier[] => {
        if (Array.isArray(response)) return response;
        if (response?.data && Array.isArray(response.data)) return response.data;
        if (response?.multipliers && Array.isArray(response.multipliers)) return response.multipliers;
        console.error(
          '/admin/language-multipliers returned an unexpected non-array shape; rendering empty. Top-level keys:',
          response && typeof response === 'object' ? Object.keys(response) : typeof response
        );
        return [];
      },
      providesTags: ['LanguageMultipliers'],
    }),
    updateLanguageMultiplier: builder.mutation<LanguageMultiplier, { id: number; multiplier: number; confidence: string }>({
      query: ({ id, ...body }) => ({
        url: `/admin/language-multipliers/${id}`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['LanguageMultipliers'],
    }),
  }),
});

export interface PartnerApiRoleResponse {
  success: boolean;
  message?: string;
  userId: number;
  role: string;
}

export interface LanguageMultiplier {
  id: number;
  languageCode: string;
  eraKey: string;
  tier: string;
  multiplier: number;
  confidence: string;
  updatedAt: string;
}

export const {
  useGetUsersQuery,
  useGetUserCountriesQuery,
  useGetUserByIdQuery,
  useGetTradeFeeQuery,
  useUpdateTradeFeeMutation,
  useGetMonthlyFeeQuery,
  useUpdateMonthlyFeeMutation,
  useGetActiveTraderDiscountQuery,
  useUpdateActiveTraderDiscountMutation,
  useGetMaintenanceModeQuery,
  useUpdateMaintenanceModeMutation,
  useGetAdminTransactionsQuery,
  useGetAdminBlocksQuery,
  useGetCronJobHistoryQuery,
  useGetCronJobTypesQuery,
  useTriggerCronJobMutation,
  useGetUserDetailsQuery,
  useAdjustUserPointsMutation,
  useImpersonateUserMutation,
  useExitImpersonationMutation,
  useBanUserMutation,
  useUnbanUserMutation,
  useDeleteUserAccountMutation,
  useGrantWriterRoleMutation,
  useRevokeWriterRoleMutation,
  useGrantPartnerApiRoleMutation,
  useRevokePartnerApiRoleMutation,
  useGetAdminEliteStatusQuery,
  useManageUserEliteMutation,
  useGetLanguageMultipliersQuery,
  useUpdateLanguageMultiplierMutation,
} = adminApi;